import {
  ImageIcon,
  LayoutLargeIcon,
  LayoutSmallIcon,
  LayoutWideIcon,
  YoutubeIcon,
  TextBodyIcon,
  TextH1Icon,
  TextH2Icon,
  TextItalicsIcon,
  TextQuoteIcon,
  TextStrikethoughIcon,
  TypeBoldIcon,
  NftIcon,
} from '@f8n/icons';
import { styled } from '@f8n-frontend/stitches';
import * as ToolbarPrimitive from '@radix-ui/react-toolbar';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import Text from 'components/base/Text';
import { STORY_COPY } from 'copy/story';

import { ApiStoryGroupSize } from 'gql/api/types-api.generated';
import {
  STORY_PARAGRAPH_PLACEHOLDER,
  storyImageContentEmptyFactory,
} from 'utils/story/story.fixtures';

import { MarksOption, StoryBlock, StoryTextBlockType } from 'types/Story';

const ToolbarRoot = styled(ToolbarPrimitive.Root, {
  padding: '$1',
  backgroundColor: '$black100',
  borderRadius: '$2',
  display: 'flex',
  alignItems: 'center',
});

const ToolbarSeparator = styled(ToolbarPrimitive.Separator, {
  height: '12px',
  width: '2px',
  backgroundColor: '$white20',
  marginX: '$1',
});

const ToolbarToggleItem = styled(ToolbarPrimitive.ToggleItem, {
  border: 'none',
  backgroundColor: '$black100',
  color: '$white100',
  transition: 'background-color $0 $ease',
  borderRadius: '$1',
  display: 'flex',
  justifyContent: 'center',
  cursor: 'pointer',

  '&[data-state=on]': {
    backgroundColor: '$white100',
    color: '$black100',
  },
  '&:focus-visible': {
    outline: '1px solid $white100',
  },
  '@hover': {
    '&:hover': {
      backgroundColor: '$white100',
      color: '$black100',
    },
  },
  '& > svg': {
    display: 'block',
  },

  variants: {
    pressed: {
      true: {
        backgroundColor: '$white100',
        color: '$black100',
      },
    },
    size: {
      0: {
        height: '$avatar2',
        width: '$avatar2',
        padding: '$1',
      },
      1: {
        height: '$avatar3',
        width: '$avatar3',
        padding: '$2',
      },
    },
  },
});

ToolbarToggleItem.defaultProps = {
  size: 0,
};

const ToolbarToggleGroup = styled(ToolbarPrimitive.ToggleGroup, {
  display: 'flex',
  gap: '$1',
});

const PersistentToolbar = styled(ToolbarRoot, {
  // variant of ToolbarRoot that is always visible
  opacity: '1 !important',
});

export const Toolbar = {
  Root: ToolbarRoot,
  ToggleItem: ToolbarToggleItem,
  ToggleGroup: ToolbarToggleGroup,
  Separator: ToolbarSeparator,
};

export function StoryStylingToolbar(props: {
  onChange(value: MarksOption[]): void;
  value: MarksOption[];
}) {
  return (
    <PersistentToolbar aria-label="Formatting options">
      <ToolbarToggleGroup
        onValueChange={props.onChange}
        value={props.value}
        type="multiple"
        aria-label="Text formatting"
      >
        <ToolbarToggleItem value="bold" aria-label="Bold">
          <TypeBoldIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem value="italic" aria-label="Italic">
          <TextItalicsIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem value="strike" aria-label="Strike-through">
          <TextStrikethoughIcon />
        </ToolbarToggleItem>
      </ToolbarToggleGroup>
    </PersistentToolbar>
  );
}

export function ContentTypeToolbar(props: {
  contentType: StoryTextBlockType;
  onContentTypeChange(value: StoryTextBlockType): void;
}) {
  return (
    <ToolbarRoot aria-label="Content types">
      <ToolbarToggleGroup
        onValueChange={(value: StoryTextBlockType) => {
          if (!value) return;
          props.onContentTypeChange(value);
        }}
        type="single"
        aria-label="Content type"
      >
        <ToolbarToggleItem
          pressed={props.contentType === 'PARAGRAPH'}
          value="PARAGRAPH"
          aria-label="Paragraph"
        >
          <TextBodyIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          pressed={props.contentType === 'H1'}
          value="H1"
          aria-label="H1"
        >
          <TextH1Icon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          pressed={props.contentType === 'H2'}
          value="H2"
          aria-label="H2"
        >
          <TextH2Icon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          pressed={props.contentType === 'BLOCKQUOTE'}
          value="BLOCKQUOTE"
          aria-label="Blockquote"
        >
          <TextQuoteIcon />
        </ToolbarToggleItem>
      </ToolbarToggleGroup>
    </ToolbarRoot>
  );
}

export function ContentLayoutToolbar(props: {
  layout: ApiStoryGroupSize;
  onLayoutChange(layout: ApiStoryGroupSize): void;
}) {
  return (
    <ToolbarRoot aria-label="Content layouts">
      <ToolbarToggleGroup type="single" aria-label="Content layout">
        <ToolbarToggleItem
          value="small"
          aria-label="Small layout"
          onClick={() => props.onLayoutChange('SMALL')}
          pressed={props.layout === 'SMALL'}
        >
          <LayoutSmallIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          value="large"
          aria-label="Large layout"
          onClick={() => props.onLayoutChange('MEDIUM')}
          pressed={props.layout === 'MEDIUM'}
        >
          <LayoutLargeIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          value="wide"
          aria-label="Wide layout"
          onClick={() => props.onLayoutChange('LARGE')}
          pressed={props.layout === 'LARGE'}
        >
          <LayoutWideIcon />
        </ToolbarToggleItem>
      </ToolbarToggleGroup>
    </ToolbarRoot>
  );
}

export function ContentManageToolbar(props: {
  onAddBlock(block: StoryBlock): void;
  hasNftsToAdd: boolean;
}) {
  return (
    <ToolbarRoot
      aria-label="Add content"
      css={{
        paddingLeft: '$5',
        paddingY: '$3',
        borderRadius: '$4',
        paddingRight: '$3',
      }}
    >
      <Text
        size={1}
        css={{
          color: '$white80',
          marginRight: '18px',
        }}
        weight="regular"
      >
        Add content
      </Text>
      <ToolbarToggleGroup
        type="single"
        aria-label="Content layout"
        css={{
          gap: '$2',
        }}
        value="unknown"
      >
        <ToolbarToggleItem
          value="text"
          aria-label="Small layout"
          size={1}
          onClick={() => {
            props.onAddBlock({
              id: uuidv4(),
              type: 'PARAGRAPH',
              content: STORY_PARAGRAPH_PLACEHOLDER,
              placeholder: STORY_COPY.TEXT_PLACEHOLDER,
            });
          }}
          pressed={false}
        >
          <TextBodyIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          value="video"
          aria-label="Large layout"
          size={1}
          onClick={() => {
            props.onAddBlock({
              id: uuidv4(),
              type: 'VIDEO',
              content: {
                state: 'EMPTY',
              },
              placeholder: null,
            });
          }}
          pressed={false}
        >
          <YoutubeIcon />
        </ToolbarToggleItem>
        <ToolbarToggleItem
          value="image"
          aria-label="Large layout"
          size={1}
          onClick={() => {
            props.onAddBlock({
              id: uuidv4(),
              type: 'IMAGE',
              content: storyImageContentEmptyFactory.get(),
              placeholder: null,
            });
          }}
          pressed={false}
        >
          <ImageIcon />
        </ToolbarToggleItem>
        {props.hasNftsToAdd && (
          <ToolbarToggleItem
            value="nft"
            aria-label="Large layout"
            size={1}
            onClick={() => {
              props.onAddBlock({
                id: uuidv4(),
                type: 'INVENTORY_ITEM',
                content: { state: 'EMPTY' },
                placeholder: null,
              });
            }}
            pressed={false}
          >
            <NftIcon />
          </ToolbarToggleItem>
        )}
      </ToolbarToggleGroup>
    </ToolbarRoot>
  );
}

export const StoryToolbarPositioner = styled('div', {
  position: 'absolute',
  bottom: 0,
  left: '50%',
  transform: 'translate(-50%, 50%)',
  zIndex: 2,
});
